import { Col, Row, Space, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { FC, Fragment, ReactElement } from 'react';
import { DashboardViewFeedbackCardMapping, DashboardViewTranscriptMapping } from '../../components/DashboardViews/ActivityFeedbackRows';
import { DataTable } from "../../components/DataTable/DataTable";
import { ExportButton } from "../../components/DataTable/ExportButton";
import { FilterButton } from "../../components/DataTable/FilterButton";
import { DateFilter } from "../../components/DataTable/Filters/DateFilter";
import { Filter, QueryFilter } from "../../components/DataTable/Filters/types";
import { SearchField } from "../../components/DataTable/SearchField";
import { PageHeader } from "../../components/PageHeader";
import { DATETIME_FORMAT, GUTTER } from "../../constants";
import { Translate, useLabels } from '../../hooks/Labels';
import { StudentExerciseAttempt } from "../../types";
import { renderActivityScore } from "../Students/components/RecentActivityAttemptsDataTable";
import { FeedbackModal } from "../../components/DashboardViews/components/Generic/FeedbackModal";
import {useOrganisation} from "../../layout/OrganisationInformationProvider";
import {PreserveQueryLink as Link} from "../../components/PreserveQueryLink";
import {OrganisationAnalyticsAccess} from "../../hooks/AnalyticsAccess";
const { Title } = Typography;


const filters: Filter<any>[] = [
    {
        attribute: 'started_datetime',
        title: 'Date Started',
        component: DateFilter,
        config: {
            filters: ['range', 'lt', 'gt']
        },
    },
    {
        attribute: 'completed_datetime',
        title: 'Date Completed',
        component: DateFilter,
        config: {
            filters: ['range', 'lt', 'gt']
        },
    }
];

const columns = (t: Translate, canViewIndividualData: boolean, canViewUserPii: boolean): ColumnsType<StudentExerciseAttempt> => ([
    ...(canViewUserPii ? [{
        title: t('student'),
        dataIndex: ['student', 'full_name'],
        key: 'student.full_name',
        fixed: 'left',
        width: 150,
        render: (text, record) =>
            record.student ? <Link to={`/students/${record.student.id}`}>{text}</Link> : 'N/A'
    }] : [{
        title: t('student'),
        dataIndex: ['student', 'id'],
        key: 'student.id',
        fixed: 'left',
        width: 150,
        render: (text, record) =>
            record.student ? <Link to={`/students/${record.student.id}`}>{`${t('student')} ${text}`}</Link> : 'N/A'
    }]) as ColumnsType<StudentExerciseAttempt>,
    {
        title: t('activity'),
        dataIndex: ['activity', 'title'],
        key: 'activity.title',
        fixed: 'left',
        width: 150,
        render: (text, record) => {
            if (!record.completed_datetime || !record.student) {
                return text;
            }

            return (
                <Link to={`/students/${record.student.id}/activity/${record.student_activity}/${record.id}`}>
                    {text}
                </Link>
            );
        }
    },
    ...(canViewIndividualData ? [{
        title: 'Score',
        dataIndex: 'score',
        key: 'score',
        width: 80,
        sorter: true,
        render: renderActivityScore
    }] : []) as ColumnsType<StudentExerciseAttempt>,
    {
        title: 'Date Started',
        dataIndex: 'started_datetime',
        key: 'started_datetime',
        sorter: true,
        width: 120,
        sortDirections: ['ascend', 'descend', 'ascend'],
        defaultSortOrder: 'descend',
        render: (value) => moment(value).format(DATETIME_FORMAT)
    },
    {
        title: 'Date Completed',
        dataIndex: 'completed_datetime',
        key: 'completed_datetime',
        sorter: true,
        width: 120,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (value) => value ? moment(value).format(DATETIME_FORMAT) : '-'
    },
    ...(canViewIndividualData && canViewUserPii ? [{
        title: 'Review',
        key: 'review',
        fixed: 'right',
        width: 110,
        render: (_: any, record: StudentExerciseAttempt) => {
            if (record.completed_datetime) {
                return record.activity.renderers.map((renderer) => {
                    const Transcript = DashboardViewTranscriptMapping[renderer.dashboard_view];
                    const Feedback = DashboardViewFeedbackCardMapping[renderer.dashboard_view];
                    const activityId = record.activity?.id ? record.activity.id.toString() : undefined;
                    return (
                        <FeedbackModal
                            title="Feedback"
                            width={Transcript && Feedback ? "90%" : undefined}
                            bodyStyle={{display: "flex"}}
                        >
                            <Row wrap={false}>
                                {
                                    Feedback &&
                                    <Col xs={24} md={Transcript ? 16 : 24} style={{
                                        overflowX: "hidden", // caused by Rows in feedback having GUTTERS which add negative margin
                                        overflowY: "auto"
                                    }}>
                                        <Feedback attemptId={record.id.toString()} activityId={activityId}/>
                                    </Col>
                                }
                                {
                                    Transcript &&
                                    <Col xs={24} md={Feedback ? 8 : 24} style={{overflow: "auto", display: "block"}}>
                                        <Row justify='center' align='middle'>
                                            <Title level={3}>{Transcript.title}</Title>
                                        </Row>

                                        <Transcript attemptId={record.id.toString()} activityId={activityId}/>
                                    </Col>
                                }
                            </Row>

                        </FeedbackModal>
                    )
                })
            }
        }
    }] : []) as ColumnsType<StudentExerciseAttempt>
]);

export const ActivityAuditPage: FC = (): ReactElement => {
    const {t} = useLabels();
    const {
        can_view_individual_data: canViewIndividualData,
        can_view_user_pii: canViewUserPii
    } = useOrganisation();
    const tableColumns = columns(t, canViewIndividualData, canViewUserPii);

    return (
        <Fragment>
            <PageHeader title={t('activity_audit')}/>
            <OrganisationAnalyticsAccess>
                <div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: GUTTER[0]}}>
                    <Space>
                        {
                            canViewUserPii
                            && <SearchField
                                placeholder={t('student')}
                            />
                        }
                        <SearchField
                            queryParam='activity_title'
                            placeholder={t('activity')}
                        />
                    </Space>
                    <Space>
                        <ExportButton
                            url='/reports/student-exercise-attempts/'
                            filenamePrefix='activity-audit'
                            defaultFilters={{
                                ordering: '-started_datetime',
                                exclude: 'activity.renderers,activity.primary_attribute,feedback'
                            }}
                            searchParamFilters={[QueryFilter.COURSES, QueryFilter.COHORTS]}
                        />
                        <FilterButton filters={filters}/>
                    </Space>
                </div>
                    <DataTable
                        columns={tableColumns}
                        query='/reports/student-exercise-attempts/'
                        defaultFilters={{
                            ordering: '-started_datetime'
                        }}
                        searchParamFilters={[QueryFilter.COURSES, QueryFilter.COHORTS]}
                        scroll={{ x: 200 }}
                    />
                </div>
            </OrganisationAnalyticsAccess>
        </Fragment>
    );
};
